import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import AffiliationRemovalAction from '@/components/contact/views/affiliation/AffiliationRemovalAction';
import { useAuth } from '@/contexts/auth-context';
import { useAffiliateActivationAction } from '@/hooks/actions/use-affiliate-activation-action';
import { fetchAffiliateDetailsByUser } from '@/hooks/queries/use-affiliate-details-query';
import { useConfirmationEscape } from '@/hooks/use-confirmation-escape';
import { Box, Button, Spinner, Stack, Text } from '@chakra-ui/react';
import { NoticePanel } from '@fluenceur/ui';
import { AffiliateProgramStatus, type AffiliateDetailsResponse } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface AffiliateQuickActionRequest {
  id: string;
  brandId: string;
  userId: string;
  programId: string;
  programName: string;
  affiliateName: string;
  action: 'pause' | 'resume' | 'remove';
}

interface Props {
  request: AffiliateQuickActionRequest;
  onClose: () => void;
  finalFocusEl: () => HTMLElement | null;
}

function StatusConfirmation({ request, onClose, finalFocusEl }: Props) {
  const { t } = useTranslation();
  const activation = useAffiliateActivationAction();
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const close = () => {
    if (!activation.isPending) onClose();
  };
  useConfirmationEscape(true, activation.isPending, close);
  const confirmKey = request.action === 'pause' ? 'deactivate' : 'activate';
  const confirm = async () => {
    if (activation.isPending) return;
    try {
      await activation.mutateAsync({
        userId: request.userId,
        programId: request.programId,
        action: request.action === 'pause' ? 'deactivate' : 'activate',
      });
      if (active.current) onClose();
    } catch {
      /* The shared action surfaces the error; the confirmation stays reviewable. */
    }
  };
  return (
    <ConfirmDialog
      isOpen
      title={t(`contactDrawer.affiliation.activation.${confirmKey}Title`)}
      description={
        <Stack gap="2">
          <Text fontWeight="semibold">{request.affiliateName}</Text>
          <Text>
            {t(`contactDrawer.affiliation.activation.${confirmKey}Desc`, {
              programName: request.programName,
            })}
          </Text>
        </Stack>
      }
      onConfirm={() => void confirm()}
      onCancel={close}
      isLoading={activation.isPending}
      confirmColorPalette={request.action === 'pause' ? 'red' : 'primary'}
      finalFocusEl={finalFocusEl}
    />
  );
}

function ReadyConfirmation({
  request,
  data,
  onClose,
  finalFocusEl,
}: Props & { data: AffiliateDetailsResponse }) {
  const { t } = useTranslation();
  // Query invalidation must not replace the exact review while its mutation completes.
  const [details] = useState(data);
  const exactTarget =
    details.program.id === request.programId &&
    details.program.brandId === request.brandId &&
    details.user.id === request.userId;
  const removable =
    exactTarget &&
    details.affiliate.canRemove === true &&
    typeof details.affiliate.lifecycleRevision === 'number';
  const statusChangeable =
    exactTarget &&
    details.affiliate.status === AffiliateProgramStatus.ENROLLED &&
    details.program.status === 'active' &&
    details.affiliate.isActive === (request.action === 'pause');
  const allowed = request.action === 'remove' ? removable : statusChangeable;
  if (!allowed)
    return (
      <ConfirmDialog
        isOpen
        title={t('affiliates.removal.programActions')}
        description={t('affiliates.membershipActions.unavailable')}
        isConfirmDisabled
        onConfirm={() => undefined}
        onCancel={onClose}
        finalFocusEl={finalFocusEl}
      />
    );
  if (request.action !== 'remove')
    return <StatusConfirmation request={request} onClose={onClose} finalFocusEl={finalFocusEl} />;
  return (
    <AffiliationRemovalAction
      initiallyOpen
      renderTrigger={() => null}
      userId={request.userId}
      programId={request.programId}
      programName={details.program.name}
      affiliateName={request.affiliateName}
      lifecycleRevision={details.affiliate.lifecycleRevision!}
      onRemoved={onClose}
      onDismiss={onClose}
      finalFocusEl={finalFocusEl}
    />
  );
}

/** One fresh per-program read at the gesture, never one read per rendered table row. */
export default function AffiliateQuickActionDialog({ request, onClose, finalFocusEl }: Props) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const currentContext = user?.currentBrandId === request.brandId;
  const query = useQuery({
    queryKey: [
      'affiliate-details',
      request.userId,
      request.programId,
      'action-review',
      request.brandId,
      request.id,
    ],
    queryFn: () => fetchAffiliateDetailsByUser(request.userId, request.programId),
    enabled: currentContext,
    retry: false,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });
  if (!currentContext) return null;
  return (
    <Box onClick={(event) => event.stopPropagation()}>
      {query.data ? (
        <ReadyConfirmation
          key={request.id}
          request={request}
          data={query.data}
          onClose={onClose}
          finalFocusEl={finalFocusEl}
        />
      ) : (
        <ConfirmDialog
          isOpen
          title={t(
            request.action === 'remove'
              ? 'affiliates.removal.title'
              : `contactDrawer.affiliation.activation.${request.action === 'pause' ? 'deactivate' : 'activate'}Title`
          )}
          description={
            <Stack gap="3">
              <Text>
                {request.affiliateName} · {request.programName}
              </Text>
              {query.isError ? (
                <NoticePanel
                  tone="warning"
                  title={t('affiliates.membershipActions.loadError')}
                  actions={
                    <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
                      {t('common.retry')}
                    </Button>
                  }
                />
              ) : (
                <Spinner size="sm" />
              )}
            </Stack>
          }
          isConfirmDisabled
          onConfirm={() => undefined}
          onCancel={onClose}
          finalFocusEl={finalFocusEl}
        />
      )}
    </Box>
  );
}
